/// <amd-module name="TFS/Admin/Common/RestClient/Security" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   vssf\client\webapi\httpclients\clientgeneratorconfigs\security.genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Security from "TFS/Admin/Common/Contracts/Security";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";

export interface ISecurityRestClient extends IVssRestClient {
    /**
     * Remove the specified ACEs from the ACL belonging to the specified token.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param token - The token whose ACL should be modified.
     * @param descriptors - String containing a list of identity descriptors separated by ',' whose entries should be removed.
     */
    removeAccessControlEntries(securityNamespaceId: string, token?: string, descriptors?: string): Promise<boolean>;
    /**
     * Add or update ACEs in the ACL for the provided token. The request body contains the target token, a list of [ACEs](https://docs.microsoft.com/en-us/rest/api/azure/devops/security/access%20control%20entries/set%20access%20control%20entries?#accesscontrolentry) and a optional merge parameter. In the case of a collision (by identity descriptor) with an existing ACE in the ACL, the "merge" parameter determines the behavior. If set, the existing ACE has its allow and deny merged with the incoming ACE's allow and deny. If unset, the existing ACE is displaced.
     * 
     * @param container - 
     * @param securityNamespaceId - Security namespace identifier.
     */
    setAccessControlEntries(container: any, securityNamespaceId: string): Promise<Security.AccessControlEntry[]>;
    /**
     * Return a list of access control lists for the specified security namespace and token. All ACLs in the security namespace will be retrieved if no optional parameters are provided.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param token - Security token
     * @param descriptors - An optional filter string containing a list of identity descriptors separated by ',' whose ACEs should be retrieved. If this is left null, entire ACLs will be returned.
     * @param includeExtendedInfo - If true, populate the extended information properties for the access control entries contained in the returned lists.
     * @param recurse - If true and this is a hierarchical namespace, return child ACLs of the specified token.
     */
    queryAccessControlLists(securityNamespaceId: string, token?: string, descriptors?: string, includeExtendedInfo?: boolean, recurse?: boolean): Promise<Security.AccessControlList[]>;
    /**
     * Remove access control lists under the specfied security namespace.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param tokens - One or more comma-separated security tokens
     * @param recurse - If true and this is a hierarchical namespace, also remove child ACLs of the specified tokens.
     */
    removeAccessControlLists(securityNamespaceId: string, tokens?: string, recurse?: boolean): Promise<boolean>;
    /**
     * Create or update one or more access control lists. All data that currently exists for the ACLs supplied will be overwritten.
     * 
     * @param accessControlLists - A list of ACLs to create or update.
     * @param securityNamespaceId - Security namespace identifier.
     */
    setAccessControlLists(accessControlLists: VS_Services.VssJsonCollectionWrapperV<Security.AccessControlListsCollection>, securityNamespaceId: string): Promise<void>;
    /**
     * Evaluates multiple permissions for the calling user.  Note: This method does not aggregate the results, nor does it short-circuit if one of the permissions evaluates to false.
     * 
     * @param evalBatch - The set of evaluation requests.
     */
    hasPermissionsBatch(evalBatch: Security.PermissionEvaluationBatch): Promise<Security.PermissionEvaluationBatch>;
    /**
     * Evaluates whether the caller has the specified permissions on the specified set of security tokens.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param permissions - Permissions to evaluate.
     * @param tokens - One or more security tokens to evaluate.
     * @param alwaysAllowAdministrators - If true and if the caller is an administrator, always return true.
     * @param delimiter - Optional security token separator. Defaults to ",".
     */
    hasPermissions(securityNamespaceId: string, permissions?: number, tokens?: string, alwaysAllowAdministrators?: boolean, delimiter?: string): Promise<boolean[]>;
    /**
     * Removes the specified permissions on a security token for a user or group.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param descriptor - Identity descriptor of the user to remove permissions for.
     * @param permissions - Permissions to remove.
     * @param token - Security token to remove permissions for.
     */
    removePermission(securityNamespaceId: string, descriptor: string, permissions?: number, token?: string): Promise<Security.AccessControlEntry>;
    /**
     * List all security namespaces or just the specified namespace.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param localOnly - If true, retrieve only local security namespaces.
     */
    querySecurityNamespaces(securityNamespaceId?: string, localOnly?: boolean): Promise<Security.SecurityNamespaceDescription[]>;
    /**
     * @param container - 
     * @param securityNamespaceId - 
     */
    setInheritFlag(container: any, securityNamespaceId: string): Promise<void>;
}

class SecurityRestClient extends RestClientBase implements ISecurityRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Remove the specified ACEs from the ACL belonging to the specified token.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param token - The token whose ACL should be modified.
     * @param descriptors - String containing a list of identity descriptors separated by ',' whose entries should be removed.
     */
    public async removeAccessControlEntries(
        securityNamespaceId: string,
        token?: string,
        descriptors?: string
        ): Promise<boolean> {

        const queryValues: any = {
            token: token,
            descriptors: descriptors
        };

        return this.beginRequest<boolean>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "_apis/AccessControlEntries/{securityNamespaceId}",
            routeValues: {
                securityNamespaceId: securityNamespaceId
            },
            queryParams: queryValues
        });
    }

    /**
     * Add or update ACEs in the ACL for the provided token. The request body contains the target token, a list of [ACEs](https://docs.microsoft.com/en-us/rest/api/azure/devops/security/access%20control%20entries/set%20access%20control%20entries?#accesscontrolentry) and a optional merge parameter. In the case of a collision (by identity descriptor) with an existing ACE in the ACL, the "merge" parameter determines the behavior. If set, the existing ACE has its allow and deny merged with the incoming ACE's allow and deny. If unset, the existing ACE is displaced.
     * 
     * @param container - 
     * @param securityNamespaceId - Security namespace identifier.
     */
    public async setAccessControlEntries(
        container: any,
        securityNamespaceId: string
        ): Promise<Security.AccessControlEntry[]> {

        return this.beginRequest<Security.AccessControlEntry[]>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "_apis/AccessControlEntries/{securityNamespaceId}",
            routeValues: {
                securityNamespaceId: securityNamespaceId
            },
            body: container
        });
    }

    /**
     * Return a list of access control lists for the specified security namespace and token. All ACLs in the security namespace will be retrieved if no optional parameters are provided.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param token - Security token
     * @param descriptors - An optional filter string containing a list of identity descriptors separated by ',' whose ACEs should be retrieved. If this is left null, entire ACLs will be returned.
     * @param includeExtendedInfo - If true, populate the extended information properties for the access control entries contained in the returned lists.
     * @param recurse - If true and this is a hierarchical namespace, return child ACLs of the specified token.
     */
    public async queryAccessControlLists(
        securityNamespaceId: string,
        token?: string,
        descriptors?: string,
        includeExtendedInfo?: boolean,
        recurse?: boolean
        ): Promise<Security.AccessControlList[]> {

        const queryValues: any = {
            token: token,
            descriptors: descriptors,
            includeExtendedInfo: includeExtendedInfo,
            recurse: recurse
        };

        return this.beginRequest<Security.AccessControlList[]>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "_apis/AccessControlLists/{securityNamespaceId}",
            routeValues: {
                securityNamespaceId: securityNamespaceId
            },
            queryParams: queryValues
        });
    }

    /**
     * Remove access control lists under the specfied security namespace.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param tokens - One or more comma-separated security tokens
     * @param recurse - If true and this is a hierarchical namespace, also remove child ACLs of the specified tokens.
     */
    public async removeAccessControlLists(
        securityNamespaceId: string,
        tokens?: string,
        recurse?: boolean
        ): Promise<boolean> {

        const queryValues: any = {
            tokens: tokens,
            recurse: recurse
        };

        return this.beginRequest<boolean>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "_apis/AccessControlLists/{securityNamespaceId}",
            routeValues: {
                securityNamespaceId: securityNamespaceId
            },
            queryParams: queryValues
        });
    }

    /**
     * Create or update one or more access control lists. All data that currently exists for the ACLs supplied will be overwritten.
     * 
     * @param accessControlLists - A list of ACLs to create or update.
     * @param securityNamespaceId - Security namespace identifier.
     */
    public async setAccessControlLists(
        accessControlLists: VS_Services.VssJsonCollectionWrapperV<Security.AccessControlListsCollection>,
        securityNamespaceId: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "_apis/AccessControlLists/{securityNamespaceId}",
            routeValues: {
                securityNamespaceId: securityNamespaceId
            },
            body: accessControlLists
        });
    }

    /**
     * Evaluates multiple permissions for the calling user.  Note: This method does not aggregate the results, nor does it short-circuit if one of the permissions evaluates to false.
     * 
     * @param evalBatch - The set of evaluation requests.
     */
    public async hasPermissionsBatch(
        evalBatch: Security.PermissionEvaluationBatch
        ): Promise<Security.PermissionEvaluationBatch> {

        return this.beginRequest<Security.PermissionEvaluationBatch>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "_apis/Security/PermissionEvaluationBatch",
            body: evalBatch
        });
    }

    /**
     * Evaluates whether the caller has the specified permissions on the specified set of security tokens.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param permissions - Permissions to evaluate.
     * @param tokens - One or more security tokens to evaluate.
     * @param alwaysAllowAdministrators - If true and if the caller is an administrator, always return true.
     * @param delimiter - Optional security token separator. Defaults to ",".
     */
    public async hasPermissions(
        securityNamespaceId: string,
        permissions?: number,
        tokens?: string,
        alwaysAllowAdministrators?: boolean,
        delimiter?: string
        ): Promise<boolean[]> {

        const queryValues: any = {
            tokens: tokens,
            alwaysAllowAdministrators: alwaysAllowAdministrators,
            delimiter: delimiter
        };

        return this.beginRequest<boolean[]>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "_apis/Permissions/{securityNamespaceId}/{permissions}",
            routeValues: {
                securityNamespaceId: securityNamespaceId,
                permissions: permissions
            },
            queryParams: queryValues
        });
    }

    /**
     * Removes the specified permissions on a security token for a user or group.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param descriptor - Identity descriptor of the user to remove permissions for.
     * @param permissions - Permissions to remove.
     * @param token - Security token to remove permissions for.
     */
    public async removePermission(
        securityNamespaceId: string,
        descriptor: string,
        permissions?: number,
        token?: string
        ): Promise<Security.AccessControlEntry> {

        const queryValues: any = {
            descriptor: descriptor,
            token: token
        };

        return this.beginRequest<Security.AccessControlEntry>({
            apiVersion: "5.0-preview.2",
            method: "DELETE",
            routeTemplate: "_apis/Permissions/{securityNamespaceId}/{permissions}",
            routeValues: {
                securityNamespaceId: securityNamespaceId,
                permissions: permissions
            },
            queryParams: queryValues
        });
    }

    /**
     * List all security namespaces or just the specified namespace.
     * 
     * @param securityNamespaceId - Security namespace identifier.
     * @param localOnly - If true, retrieve only local security namespaces.
     */
    public async querySecurityNamespaces(
        securityNamespaceId?: string,
        localOnly?: boolean
        ): Promise<Security.SecurityNamespaceDescription[]> {

        const queryValues: any = {
            localOnly: localOnly
        };

        return this.beginRequest<Security.SecurityNamespaceDescription[]>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "_apis/SecurityNamespaces/{securityNamespaceId}",
            routeValues: {
                securityNamespaceId: securityNamespaceId
            },
            queryParams: queryValues
        });
    }

    /**
     * @param container - 
     * @param securityNamespaceId - 
     */
    public async setInheritFlag(
        container: any,
        securityNamespaceId: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "_apis/SecurityNamespaces/{securityNamespaceId}",
            routeValues: {
                securityNamespaceId: securityNamespaceId
            },
            body: container
        });
    }

}

export const SecurityClientName = "Security-Admin.ISecurityRestClient";

export function getSecurityClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): ISecurityRestClient {
    return pageContext.getRestClient<ISecurityRestClient>(SecurityClientName, options);
}

RestClients.add(SecurityClientName, { 
    factory: SecurityRestClient
});
